import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";

export interface OrderSummaryStatisticsQuery {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: any[];
}

export interface OrderListQuery {
  page: number;
  pageSize: number;
  filterType: 'all' | 'from_quotation' | 'direct';
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: any[];
  sorting?: any[];
  filters?: any[];
}

export interface DailyOrderTrendQuery {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: any[];
}

// Fetch order summary statistics (for the summary cards)
export const fetchOrderSummaryStatistics = createAsyncThunk(
  "orderSummary/fetchStatistics",
  async (query: OrderSummaryStatisticsQuery, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      // Add date filters
      if (query.dateFrom) {
        params.append("date_from", query.dateFrom.toISOString().split('T')[0]);
      }
      if (query.dateTo) {
        params.append("date_to", query.dateTo.toISOString().split('T')[0]);
      }

      // Add warehouse filters (exclude "all" option)
      if (query.warehouse && query.warehouse.length > 0) {
        const warehouseIds = query.warehouse
          .map((w: any) => w.warehouse_id)
          .filter((id: any) => id !== "all" && id !== "All Warehouses");
        
        if (warehouseIds.length > 0) {
          if (warehouseIds.length === 1) {
            params.append("warehouse_id", warehouseIds[0].toString());
          } else {
            params.append("warehouse_id", warehouseIds.join(','));
          }
        }
      }

      const url = `${api?.dashboad?.total_order_statistics}${params.toString() ? `?${params.toString()}` : ''}`;
      const quotation_order_url = `${api?.dashboad?.total_quotation_order_statistics}${params.toString() ? `?${params.toString()}` : ''}`;
      const direct_order_url = `${api?.dashboad?.total_direct_order_statistics}${params.toString() ? `?${params.toString()}` : ''}`;

      // Call all three APIs in parallel
      const [response, quotation_order_response, direct_order_response] = await Promise.all([
        Axios.get(url),
        Axios.get(quotation_order_url),
        Axios.get(direct_order_url),
      ]);

      if (response.data?.data && quotation_order_response.data?.data && direct_order_response.data?.data) {
        return {
          total_orders: response?.data?.data?.total_orders || 0,
          from_quotation: quotation_order_response?.data?.data?.total_orders || 0,
          direct_orders: direct_order_response?.data?.data?.total_orders || 0,
          confirmed: response?.data?.data?.confirmed || 0,
          completed: response?.data?.data?.total_dispatched || 0,
          cancelled: response?.data?.data?.total_cancelled || 0,
          processing: response?.data?.data?.total_remaining || 0,
          from_quotation_completed: quotation_order_response?.data?.data?.total_dispatched || 0,
          from_quotation_cancelled: quotation_order_response?.data?.data?.total_cancelled || 0,
          from_quotation_processing: quotation_order_response?.data?.data?.total_remaining || 0,
          direct_completed: direct_order_response?.data?.data?.total_dispatched || 0,
          direct_cancelled: direct_order_response?.data?.data?.total_cancelled || 0,
          direct_processing: direct_order_response?.data?.data?.total_remaining || 0,
        };
      }

      // Return default values if data is missing
      return {
        total_orders: 0,
        from_quotation: 0,
        direct_orders: 0,
        confirmed: 0,
        processing: 0,
        completed: 0,
        cancelled: 0,
        from_quotation_completed: 0,
        from_quotation_cancelled: 0,
        from_quotation_processing: 0,
        direct_completed: 0,
        direct_cancelled: 0,
        direct_processing: 0,
      };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || "Network Error");
    }
  }
);

// Fetch order list (for the table)
export const fetchOrderList = createAsyncThunk(
  "orderSummary/fetchOrderList",
  async (query: OrderListQuery, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      // Pagination
      params.append("page", query.page.toString());
      params.append("page_size", query.pageSize.toString());

      // Date filters
      if (query.dateFrom) {
        params.append("date_from", query.dateFrom.toISOString().split('T')[0]);
      }
      if (query.dateTo) {
        params.append("date_to", query.dateTo.toISOString().split('T')[0]);
      }

      // Warehouse filters
      if (query.warehouse && query.warehouse.length > 0) {
        const warehouseIds = query.warehouse
          .map((w: any) => w.warehouse_id)
          .filter((id: any) => id !== "all" && id !== "All Warehouses");
        
        if (warehouseIds.length > 0) {
          if (warehouseIds.length === 1) {
            params.append("warehouse_id", warehouseIds[0].toString());
          } else {
            params.append("warehouse_id", warehouseIds.join(','));
          }
        }
      }

      // Sorting
      if (query.sorting && query.sorting.length > 0) {
        const sortField = query.sorting[0].id;
        const sortDirection = query.sorting[0].desc ? '-' : '';
        params.append("ordering", `${sortDirection}${sortField}`);
      }

      // Column filters
      if (Array.isArray(query.filters)) {
        query.filters.forEach((filter: any) => {
          if (filter.filterField && filter.filterValue !== undefined && filter.filterValue !== null) {
            // Handle date range filters
            if (typeof filter.filterValue === "object" && filter.filterValue !== null) {
              const { from_date, to_date } = filter.filterValue;
              if (from_date && (filter.filterField === "created_at" || filter.filterField === "updated_at" || filter.filterField === "order_date" || filter.filterField === "expected_delivery_date")) {
                params.append(`${filter.filterField}_from`, from_date);
              }
              if (to_date && (filter.filterField === "created_at" || filter.filterField === "updated_at" || filter.filterField === "order_date" || filter.filterField === "expected_delivery_date")) {
                params.append(`${filter.filterField}_to`, to_date);
              }
            } else {
              params.append(filter.filterField, String(filter.filterValue));
            }
          }
        });
      }

      // Select API endpoint based on filter type
      let apiUrl = '';
      if (query.filterType === 'from_quotation') {
        apiUrl = api?.dashboad?.quotation_order_list || '';
      } else if (query.filterType === 'direct') {
        apiUrl = api?.dashboad?.direct_order_list || '';
      } else {
        // For 'all' (Total Orders), use the main order_list endpoint
        apiUrl = api?.dashboad?.order_list || '';
      }

      const url = `${apiUrl}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await Axios.get(url);

      if (response.data?.data) {
        return {
          data: response.data.data || [],
          totalRecords: response.data.totalRecords || response.data.count || 0,
        };
      }

      return {
        data: [],
        totalRecords: 0,
      };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || "Network Error");
    }
  }
);

// Fetch daily order trend data
export const fetchDailyOrderTrend = createAsyncThunk(
  "orderSummary/fetchDailyOrderTrend",
  async (query: DailyOrderTrendQuery, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      // Add date filters
      if (query.dateFrom) {
        params.append("date_from", query.dateFrom.toISOString().split('T')[0]);
      }
      if (query.dateTo) {
        params.append("date_to", query.dateTo.toISOString().split('T')[0]);
      }

      // Add warehouse filters (exclude "all" option)
      if (query.warehouse && query.warehouse.length > 0) {
        const warehouseIds = query.warehouse
          .map((w: any) => w.warehouse_id)
          .filter((id: any) => id !== "all" && id !== "All Warehouses");
        
        if (warehouseIds.length > 0) {
          if (warehouseIds.length === 1) {
            params.append("warehouse_id", warehouseIds[0].toString());
          } else {
            params.append("warehouse_id", warehouseIds.join(','));
          }
        }
      }

      const url = `${api?.dashboad?.daily_order_trend}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await Axios.get(url);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || "Network Error");
    }
  }
);

